import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, subHours, differenceInMinutes } from 'date-fns';
import missionService from '../services/missionService';
import { useAuth } from '../contexts/AuthContext';
import { Mission, MissionStatus, Priority } from '../types';
import { formatMissionType, formatMissionStatus, formatPriority, formatTimeAgo } from '../utils/formatUtils';
import {
  ArrowLeftIcon,
  PrinterIcon,
  ExclamationTriangleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';

const ShiftHandoff: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(true);
  const [handoffNotes, setHandoffNotes] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await missionService.getMissions({ viewMode: 'all' });
        setMissions(response.missions);
      } catch (error) {
        console.error('Failed to fetch missions:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const activeMissions = missions.filter(m =>
    ![MissionStatus.CLOSED, MissionStatus.CANCELLED, MissionStatus.MISSION_COMPLETE].includes(m.status as MissionStatus)
  );

  const completedThisShift = missions.filter(m => {
    if (m.status !== MissionStatus.MISSION_COMPLETE && m.status !== MissionStatus.CLOSED) return false;
    const completed = m.dateClosed || m.timeMissionComplete;
    return completed && new Date(completed) >= subHours(new Date(), 12);
  });

  const cancelledThisShift = missions.filter(m => {
    if (m.status !== MissionStatus.CANCELLED) return false;
    const updated = new Date(m.dateUpdated);
    return updated >= subHours(new Date(), 12);
  });

  const recentComments = missions
    .flatMap(m => (m.comments || []).map(c => ({ ...c, missionNumber: m.missionNumber, missionId: m.id })))
    .filter(c => new Date(c.timestamp) >= subHours(new Date(), 12))
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 15);

  // Missions that might need attention: active for >2 hours or critical
  const needsAttention = activeMissions.filter(m => {
    if (m.priority === Priority.CRITICAL) return true;
    if (!m.timeReceived) return false;
    return differenceInMinutes(new Date(), new Date(m.timeReceived)) > 120;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  const MissionRow: React.FC<{ mission: Mission; showElapsed?: boolean }> = ({ mission, showElapsed }) => {
    const elapsed = mission.timeReceived
      ? formatTimeAgo(mission.timeReceived)
      : '--';

    return (
      <tr
        className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
        onClick={() => navigate(`/mission/${mission.id}`)}
      >
        <td className="px-3 py-2 font-medium text-sm">{mission.missionNumber}</td>
        <td className="px-3 py-2 text-sm">
          <span className={`inline-flex items-center gap-1 ${
            mission.priority === Priority.CRITICAL ? 'text-red-600 font-semibold' :
            mission.priority === Priority.URGENT ? 'text-yellow-600' : 'text-gray-600'
          }`}>
            {mission.priority === Priority.CRITICAL && <ExclamationTriangleIcon className="h-3.5 w-3.5" />}
            {formatPriority(mission.priority)}
          </span>
        </td>
        <td className="px-3 py-2 text-sm">{formatMissionStatus(mission.status)}</td>
        <td className="px-3 py-2 text-sm text-gray-600">{formatMissionType(mission.missionType)}</td>
        <td className="px-3 py-2 text-sm text-gray-600 max-w-[200px] truncate">{mission.sceneLocation || '--'}</td>
        <td className="px-3 py-2 text-sm text-gray-600">{mission.primaryBase?.code || '--'}</td>
        <td className="px-3 py-2 text-sm text-center">{mission.patients?.length ?? 0}</td>
        {showElapsed && <td className="px-3 py-2 text-sm text-gray-500">{elapsed}</td>}
      </tr>
    );
  };

  const TableHeader: React.FC<{ showElapsed?: boolean }> = ({ showElapsed }) => (
    <thead>
      <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-300">
        <th className="px-3 py-2">Mission</th>
        <th className="px-3 py-2">Priority</th>
        <th className="px-3 py-2">Status</th>
        <th className="px-3 py-2">Type</th>
        <th className="px-3 py-2">Location</th>
        <th className="px-3 py-2">Base</th>
        <th className="px-3 py-2">Pts</th>
        {showElapsed && <th className="px-3 py-2">Received</th>}
      </tr>
    </thead>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 no-print">
        <div className="px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
              <ArrowLeftIcon className="h-4 w-4 mr-2" /> Back
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Shift Handoff</h1>
              <p className="text-sm text-gray-500">{format(new Date(), 'EEEE, dd MMMM yyyy HH:mm')} — {user?.fullName}</p>
            </div>
          </div>
          <button onClick={() => window.print()} className="btn btn-secondary no-print">
            <PrinterIcon className="h-4 w-4 mr-2" /> Print
          </button>
        </div>
      </header>

      <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6 max-w-6xl mx-auto">

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Active Missions</p>
            <p className="text-3xl font-bold text-gray-900">{activeMissions.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Needs Attention</p>
            <p className="text-3xl font-bold text-red-600">{needsAttention.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Completed This Shift</p>
            <p className="text-3xl font-bold text-green-600">{completedThisShift.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Cancelled This Shift</p>
            <p className="text-3xl font-bold text-gray-500">{cancelledThisShift.length}</p>
          </div>
        </div>

        {/* Needs Attention */}
        {needsAttention.length > 0 && (
          <div className="bg-red-50 rounded-lg border border-red-200 p-6">
            <h2 className="text-lg font-semibold text-red-800 flex items-center gap-2 mb-4">
              <ExclamationTriangleIcon className="h-5 w-5" />
              Needs Attention ({needsAttention.length})
            </h2>
            <p className="text-sm text-red-700 mb-3">Critical missions or active for more than 2 hours.</p>
            <table className="w-full text-sm">
              <TableHeader showElapsed />
              <tbody>{needsAttention.map(m => <MissionRow key={m.id} mission={m} showElapsed />)}</tbody>
            </table>
          </div>
        )}

        {/* Active Missions */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <ClockIcon className="h-5 w-5 text-gray-400" />
            All Active Missions ({activeMissions.length})
          </h2>
          {activeMissions.length > 0 ? (
            <table className="w-full text-sm">
              <TableHeader showElapsed />
              <tbody>{activeMissions.map(m => <MissionRow key={m.id} mission={m} showElapsed />)}</tbody>
            </table>
          ) : (
            <p className="text-gray-500 text-sm">No active missions.</p>
          )}
        </div>

        {/* Completed This Shift */}
        {completedThisShift.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4 text-green-700">Completed This Shift ({completedThisShift.length})</h2>
            <table className="w-full text-sm">
              <TableHeader />
              <tbody>{completedThisShift.map(m => <MissionRow key={m.id} mission={m} />)}</tbody>
            </table>
          </div>
        )}

        {/* Recent Communications */}
        {recentComments.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Recent Communications (Last 12h)</h2>
            <div className="space-y-2 text-sm max-h-64 overflow-y-auto">
              {recentComments.map(c => (
                <div
                  key={c.id}
                  className="flex items-start gap-3 py-1.5 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate(`/mission/${c.missionId}`)}
                >
                  <span className="font-mono text-xs text-gray-400 w-14 flex-shrink-0 pt-0.5">
                    {format(new Date(c.timestamp), 'HH:mm')}
                  </span>
                  <span className="font-medium text-gray-700 w-28 flex-shrink-0">{c.missionNumber}</span>
                  <span className="font-medium text-gray-500 w-8 flex-shrink-0">[{c.dispatcherInitials}]</span>
                  <span className="text-gray-600 flex-1">{c.commentText}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Handoff Notes */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-3">Handoff Notes</h2>
          <p className="text-sm text-gray-500 mb-3">
            Add any notes for the incoming shift — pending actions, follow-ups, equipment issues, weather concerns.
          </p>
          <textarea
            value={handoffNotes}
            onChange={(e) => setHandoffNotes(e.target.value)}
            className="form-input w-full"
            rows={5}
            placeholder="Notes for incoming shift..."
          />
          <p className="text-xs text-gray-400 mt-2">
            These notes are included when you print this page. They are not saved to the system.
          </p>
        </div>

        {/* Print Footer */}
        <div className="hidden print:block text-xs text-gray-500 border-t pt-4 mt-8 flex justify-between">
          <span>Shift Handoff Report — {format(new Date(), 'dd/MM/yyyy HH:mm')}</span>
          <span>Prepared by: {user?.fullName}</span>
          <span>TEAAM HEMS Dispatch</span>
        </div>
      </div>
    </div>
  );
};

export default ShiftHandoff;
